import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { ReactComponent as Add } from '$icons/uncolored/Add.svg';
import { ReactComponent as Delete } from '$icons/TrashIcon.svg';
import { ReactComponent as Copy } from '$icons/copy.svg';
import { useState } from 'react';
import Spinner from '$components/Spinner/Spinner';
import {
  GetOneSmartInterviewOutput,
  useDeleteSmartInterviewMutation,
  useGetAllSmartInterviewsQuery,
  useLazyGetOneSmartInterviewQuery,
} from '$store/modules/smart-interviews';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { successHandler } from '$store/utils';
import copyToClipboard from '$utils/copyToClipboard';
import SmartInterviewEditor from './SmartInterviewEditor';

export const SmartInterviews = () => {
  const [
    getOneSmartInterview,
    {
      isLoading: isLoadingOneSmartInterview,
      isFetching: isFechingOneSmartInterview,
    },
  ] = useLazyGetOneSmartInterviewQuery();
  const { data, isLoading } = useGetAllSmartInterviewsQuery();
  const [deleteSmartInterview, { isLoading: isLoadingDelete }] =
    useDeleteSmartInterviewMutation();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSmartInterview, setSelectedSmartInterview] = useState<
    { type: 'new' | 'update'; data: GetOneSmartInterviewOutput } | undefined
  >();

  const addNewSmartInterviewHandler = () => {
    setSelectedSmartInterview({
      type: 'new',
      data: {
        Name: '',
        Description: '',
        Questions: [''],
        WelcomeMessages: [''],
      },
    });
  };

  const handleDeleteSmartInterview = async () => {
    await deleteSmartInterview(selectedSmartInterview?.data?.Id as number).then(
      () => {
        setOpenDeleteModal(false);
        successHandler('Smart Interview deleted', true, true);
        setSelectedSmartInterview(undefined);
      }
    );
  };

  const copySmartInterviewIdHandler = async (smartInterviewId: string) => {
    await copyToClipboard(
      `${import.meta.env.REACT_APP_SPK_URL}/smartinterview?id=${smartInterviewId}`
    );
    successHandler(
      'Smart Interview Id copied successfully into clipboard',
      true,
      true
    );
  };

  return (
    <>
      {isLoading ? (
        <Spinner secondary width="100%" height="100%" />
      ) : (
        <div
          data-testid="smartInterviews"
          className="container mx-auto mt-8 flex flex-row gap-4"
        >
          <div className="sticky top-2 w-1/4 rounded border-gray-200 border py-4 bg-white box-border overflow-hidden flex flex-col max-h-[calc(100vh-8rem)]">
            <div className="border-b mt-2 mb-4 pb-2 mx-4">
              <h1 className="font-bold text-2xl text-nowrap items-center flex flex-row gap-2">
                Smart Interviews
                <button
                  type="button"
                  className="bg-internal-main rounded-full p-1"
                  onClick={addNewSmartInterviewHandler}
                >
                  <Add className="fill-white w-3 h-3" />
                </button>
              </h1>
              <a
                target="_blank"
                rel="noreferrer"
                className="text-xs text-internal-link"
                href="https://simera.atlassian.net/wiki/spaces/SIMERA/pages/391380993/Add+edit+and+remove+Smart+Interviews"
              >
                Need help? Read this guide
              </a>
            </div>
            <div
              className="flex flex-col flex-grow gap-y-4 max-h-full overflow-auto px-4"
              data-testid="smartInterviewListContainer"
            >
              {data &&
                data.map((singleSmartInterview) => {
                  return (
                    <button
                      key={`smartInterview_${singleSmartInterview.Id}`}
                      type="button"
                      className={`rounded border border-internal-main border-solid text-left p-2 flex flex-row items-center justify-between group ${
                        selectedSmartInterview?.data.Id ===
                        singleSmartInterview.Id
                          ? 'bg-internal-main/20'
                          : 'hover:bg-internal-main/10'
                      }`}
                      onClick={async () => {
                        await getOneSmartInterview({
                          smartInterviewId: singleSmartInterview.Id as number,
                        }).then((resp) => {
                          if ('data' in resp)
                            setSelectedSmartInterview({
                              type: 'update',
                              data: resp.data as GetOneSmartInterviewOutput,
                            });
                        });
                      }}
                    >
                      <div className="flex flex-1 flex-col mr-2">
                        <div className="text-base font-semibold mb-2 flex flex-row justify-between items-start">
                          <span>{singleSmartInterview.Name}</span>
                          <div>
                            <div
                              role="button"
                              onKeyDown={() => {}}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                copySmartInterviewIdHandler(
                                  `${singleSmartInterview.Id}`
                                );
                              }}
                              tabIndex={0}
                              className="py-1 px-2 text-xs bg-internal-main/10 rounded inline-flex gap-2 text-nowrap"
                            >
                              <span className="text-nowrap">
                                ID: {singleSmartInterview.Id}
                              </span>
                              <Copy className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                        <div className="text-sm mb-2 break-words text-justify">
                          {singleSmartInterview.Description}
                        </div>
                      </div>
                      <Arrow
                        className={`fill-internal-main/50 w-6 h-6 -rotate-90 ${
                          selectedSmartInterview?.data.Id !==
                            singleSmartInterview.Id &&
                          'opacity-0 group-hover:opacity-100'
                        }`}
                      />
                    </button>
                  );
                })}
            </div>
          </div>
          <div
            className="w-3/4 bg-white p-4 border border-gray-200 rounded flex-grow flex flex-col"
            data-testid="smartInterviewViewer"
          >
            <div className="border-b mt-2 pb-2 flex flex-row justify-between items-center">
              <h1 className="font-bold text-2xl">
                Smart Interview Information
              </h1>
              {selectedSmartInterview?.data?.Id && (
                <button
                  type="button"
                  className="relative w-10 h-10 flex items-center justify-center border border-gray-400 border-solid rounded-full hover:w-64 transition-all duration-300 overflow-hidden group hover:bg-red-700 hover:border-none"
                  onClick={() => setOpenDeleteModal(true)}
                >
                  <span className="text-xl group-hover:hidden">
                    <Delete className="fill-gray-400" />
                  </span>

                  <span className="absolute opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap text-nowrap flex flex-row items-center gap-2">
                    <Delete className="fill-white" />
                    <span className="text-sm text-white">
                      Delete this Smart Interview
                    </span>
                  </span>
                </button>
              )}
            </div>
            {selectedSmartInterview &&
              (isLoadingOneSmartInterview || isFechingOneSmartInterview) && (
                <div className="min-h-48">
                  <Spinner secondary width="100%" height="100%" />
                </div>
              )}
            {selectedSmartInterview &&
              !isFechingOneSmartInterview &&
              !isLoadingOneSmartInterview && (
                <SmartInterviewEditor
                  smartInterviewData={selectedSmartInterview.data}
                  onCancel={() => setSelectedSmartInterview(undefined)}
                  onCreateOrUpdate={(
                    newInterview: GetOneSmartInterviewOutput
                  ) => {
                    setSelectedSmartInterview({
                      type: 'update',
                      data: newInterview,
                    });
                  }}
                  action={selectedSmartInterview.type}
                />
              )}
            {!selectedSmartInterview && (
              <div className="flex flex-col items-center justify-center gap-2 flex-grow min-h-48">
                <button
                  type="button"
                  className="bg-internal-main/10 rounded-full px-4 py-1 text-internal-main font-bold flex flex-row items-center gap-2"
                  onClick={addNewSmartInterviewHandler}
                >
                  <Add className="fill-internal-main" />
                  Create a new Smart Interview
                </button>
                <span className="text-sm">
                  Or select a Smart Interview to edit it
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <ModalInTailwind
        isOpen={openDeleteModal}
        closeModal={() => setOpenDeleteModal(false)}
      >
        <form onSubmit={() => false}>
          <fieldset disabled={isLoadingDelete}>
            <div className="bg-white rounded-xl shadow p-8">
              <h1 className="text-xl font-semibold mb-4">
                Are you sure you want to delete this Smart Interview?
              </h1>
              <div className="flex flex-row gap-8 items-center justify-center">
                <button
                  type="button"
                  className="rounded px-4 py-1 font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-internal-main"
                  onClick={() => setOpenDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="border border-gray-200 border-solid rounded px-4 py-1 hover:bg-red-600 bg-red-500 text-white font-semibold shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDeleteSmartInterview}
                >
                  {isLoadingDelete ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      </ModalInTailwind>
    </>
  );
};
